import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | null;
interface LegalModalProps {
  type: ModalType;
  onClose: () => void;
}
function LegalModal({
  type,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (type) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [type]);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const content = {
    terms: {
      title: 'Términos y Condiciones',
      body: <>
          <p data-id="element-71">
            <strong data-id="element-72">1. Aceptación de los términos.</strong> Al adquirir el eBook
            "Método Fuego Cero" usted acepta estos términos en su totalidad. Si
            no está de acuerdo, le pedimos no realizar la compra.
          </p>
          <p data-id="element-73">
            <strong data-id="element-74">2. Naturaleza del producto.</strong> Este es un producto
            digital descargable. No se envía material físico. El acceso es
            inmediato luego de confirmado el pago.
          </p>
          <p data-id="element-75">
            <strong data-id="element-76">3. Uso personal.</strong> El contenido del eBook, recetas y
            protocolos son para uso personal del comprador. No está permitido
            revender, distribuir ni reproducir el material sin autorización
            escrita.
          </p>
          <p data-id="element-77">
            <strong data-id="element-78">4. Resultados.</strong> Los resultados varían según la
            persona, sus hábitos, condiciones de salud previas y constancia.
            Este material es educativo y no reemplaza la consulta con un
            profesional de la salud.
          </p>
          <p data-id="element-79">
            <strong data-id="element-80">5. Política de reembolso.</strong> Por tratarse de un
            producto digital de descarga inmediata, todas las ventas son
            finales. Para casos excepcionales contactar al equipo de soporte.
          </p>
          <p data-id="element-81">
            <strong data-id="element-82">6. Aviso médico.</strong> Si está embarazada, en lactancia,
            bajo tratamiento médico o tiene alguna condición de salud, consulte
            a su médico antes de aplicar el protocolo.
          </p>
          <p data-id="element-83">
            <strong data-id="element-84">7. Modificaciones.</strong> Nos reservamos el derecho de
            actualizar estos términos en cualquier momento. La versión vigente
            será siempre la publicada en este sitio.
          </p>
        </>
    },
    privacy: {
      title: 'Política de Privacidad',
      body: <>
          <p data-id="element-85">
            <strong data-id="element-86">1. Información que recopilamos.</strong> Recopilamos los
            datos necesarios para procesar tu compra: nombre, correo electrónico
            y datos de pago (estos últimos son procesados de forma segura por
            nuestra plataforma de pagos y no quedan almacenados en nuestros
            servidores).
          </p>
          <p data-id="element-87">
            <strong data-id="element-88">2. Uso de la información.</strong> Usamos tus datos
            exclusivamente para: entregarte el producto digital, enviarte el
            comprobante de compra y enviarte comunicaciones relacionadas con el
            método (de las cuales puedes darte de baja en cualquier momento).
          </p>
          <p data-id="element-89">
            <strong data-id="element-90">3. Protección de datos.</strong> Tu información está
            protegida y no será compartida ni vendida a terceros. Cumplimos con
            la Ley N° 19.628 sobre Protección de la Vida Privada de Chile.
          </p>
          <p data-id="element-91">
            <strong data-id="element-92">4. Cookies.</strong> Este sitio puede usar cookies para
            mejorar tu experiencia de navegación. Puedes desactivarlas desde la
            configuración de tu navegador.
          </p>
          <p data-id="element-93">
            <strong data-id="element-94">5. Tus derechos.</strong> Tienes derecho a acceder,
            rectificar o eliminar tus datos personales en cualquier momento
            escribiéndonos al canal de soporte.
          </p>
          <p data-id="element-95">
            <strong data-id="element-96">6. Cambios.</strong> Esta política puede actualizarse. La
            versión vigente será siempre la publicada en este sitio.
          </p>
        </>
    }
  };
  return <AnimatePresence data-id="element-97">
      {type && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-brand-navy/70 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-98">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="bg-brand-cream w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-brand-gold/20" data-id="element-99">
            <div className="flex items-center justify-between p-6 border-b border-brand-navy/10 bg-white" data-id="element-100">
              <h2 id="legal-modal-title" className="font-serif font-bold text-2xl text-brand-navy" data-id="element-101">
                {content[type].title}
              </h2>
              <button onClick={onClose} aria-label="Cerrar" className="p-2 rounded-full hover:bg-brand-navy/5 transition-colors text-brand-navy" data-id="element-102">
                <X className="w-5 h-5" data-id="element-103" />
              </button>
            </div>
            <div className="overflow-y-auto p-6 sm:p-8 space-y-4 text-brand-navy/80 text-sm leading-relaxed" data-id="element-104">
              {content[type].body}
            </div>
            <div className="p-4 border-t border-brand-navy/10 bg-white flex justify-end" data-id="element-105">
              <button onClick={onClose} className="px-5 py-2 bg-brand-navy text-brand-cream rounded-lg font-medium text-sm hover:bg-brand-navyLight transition-colors" data-id="element-106">
                Cerrar
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [modal, setModal] = useState<ModalType>(null);
  return <footer className="bg-brand-navy text-brand-cream/60 py-12 border-t border-white/10" data-id="element-107">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-108">
        <div className="text-center md:text-left" data-id="element-109">
          <p className="font-serif font-bold text-xl text-brand-gold mb-2" data-id="element-110">
            Método Fuego Cero
          </p>
          <p className="text-sm" data-id="element-111">
            © {new Date().getFullYear()} Todos los derechos reservados.
          </p>
          <p className="text-xs mt-1 opacity-70" data-id="element-112">
            Este producto es una descarga digital. No se enviará ningún producto
            físico.
          </p>
        </div>

        <div className="flex gap-6 text-sm" data-id="element-113">
          <button onClick={() => setModal('terms')} className="hover:text-brand-gold transition-colors" data-id="element-114">
            Términos y Condiciones
          </button>
          <button onClick={() => setModal('privacy')} className="hover:text-brand-gold transition-colors" data-id="element-115">
            Política de Privacidad
          </button>
        </div>
      </div>

      <LegalModal type={modal} onClose={() => setModal(null)} data-id="element-116" />
    </footer>;
}